import { accessToken } from './current-user';
import { reloadForBuildoutRequestFormAccessDenied } from './buildout-request-form/access-denied-redirect';

class ApiRequest {
  constructor(url, success, failure, acceptJsonHeaders, abortSignal) {
    this.url = url;
    this.success = success;
    this.failure = failure;
    this.acceptJsonHeaders = (typeof acceptJsonHeaders === 'undefined') ? false : acceptJsonHeaders;
    this.abortSignal = abortSignal || undefined;
  }

  get(data) {
    if (data) {
      this.url += `?${Object.keys(data).map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`).join('&')}`;
    }

    this.fetch('get');
  }

  patch(data) {
    this.fetch('PATCH', data);
  }

  post(data) {
    this.fetch('post', data);
  }

  put(data) {
    this.fetch('put', data);
  }

  delete(data = undefined) {
    this.fetch('delete', data);
  }

  base64(data) {
    this.baseFetch('get', data)
      .then((response) => response.blob())
      .then((blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then((base64) => this.returnData({ data: base64 }))
      .catch((error) => {
        this.handleError(error);
      });
  }

  baseFetch(method, data) {
    const headers = { Authorization: ApiRequest.getAuthorizationHeader() };
    let body = data;

    if (this.acceptJsonHeaders === true) {
      headers.Accept = 'application/json';
    }

    if (!(data instanceof FormData)) {
      headers.Accept = 'application/json';
      headers['Content-Type'] = 'application/json';

      body = JSON.stringify(data);
    }

    // TODO

    const fetchOptions = {
      headers,
      method,
      body,
    };

    if (this.abortSignal) {
      fetchOptions.signal = this.abortSignal;
    }

    return fetch(this.url, fetchOptions).then((response) => ApiRequest.checkStatus(response));
  }

  fetch(method, data) {
    this.baseFetch(method, data)
      .then((response) => ApiRequest.getJson(response))
      .then((json) => this.returnData(json))
      .catch((error) => {
        this.handleError(error);
      });
  }

  static getAuthorizationHeader() {
    const token = this.getAccessToken();

    return `Bearer ${token}`;
  }

  static getAccessToken() {
    return accessToken();
  }

  static checkStatus(response) {
    if (response.status < 200 || response.status > 299) {
      const error = new Error(response.statusText);
      error.response = response;
      throw error;
    }

    return response;
  }

  static getJson(response) {
    if (response.status === 204) {
      return { data: null };
    }

    return response.json();
  }

  returnData(json) {
    this.success(json.data, json.meta);

    return json;
  }

  handleError(err) {
    if (err?.name === 'AbortError') {
      return;
    }

    // BRF session-match / role denials: reload so the web route can abort(403).
    if (reloadForBuildoutRequestFormAccessDenied(this.url, err)) {
      return;
    }

    console.error(err);

    if (typeof this.failure === 'function') {
      this.failure(err);
    }
  }

  static parseFailureResponse(failureResponse) {
    const errorClasses = {};
    const alertMessages = [];
    Object.entries(failureResponse).forEach(([field, errorPayload]) => {
      errorClasses[field] = 'input--error';
      const errors = Array.isArray(errorPayload) ? errorPayload : [errorPayload];
      errors.forEach((errorMessage) => {
        alertMessages.push({ message: errorMessage, level: 'error' });
      });
    });
    return {
      errorClasses,
      alertMessages,
    };
  }

  /**
   * Safely read JSON from an ApiRequest failure.
   * Returns null when error.response / response.json is missing, or when parsing fails.
   * Callers can apply this to failure handlers that currently assume error.response.json().
   */
  static async readErrorResponseJson(error) {
    try {
      if (!error?.response || typeof error.response.json !== 'function') {
        return null;
      }

      return await error.response.json();
    } catch {
      return null;
    }
  }

  static async handleApiErrorResponse(apiErrorResponse) {
    let errorClasses = {};
    let alertMessages = [];
    const failureResponse = await ApiRequest.readErrorResponseJson(apiErrorResponse);

    if (failureResponse && Object.keys(failureResponse).length) {
      const {
        errorClasses: classes,
        alertMessages: alerts,
      } = ApiRequest.parseFailureResponse(failureResponse);
      errorClasses = classes;
      alertMessages = alerts;
    }

    if (!alertMessages.length) {
      alertMessages.push({
        message: 'There were some issues with the request. Please try again.',
        level: 'error',
      });
    }

    return {
      errorClasses,
      alertMessages,
    };
  }
}

export default ApiRequest;
